import { CreateLocationRequest, CreatePointRequest, CustomShape, GeoJsonStyle, GeoJsonType, GetPointResponse, MapDrawer, ObjectId, TopLevelShapeHolder } from '../types';
import { ZERO_OBJECT_ID } from '../helpers/constants';
import { LitElement, PropertyValueMap, css, html } from 'lit';
import { PreciseShapeUnit, addShapeToMap, codeToCustomShapes, countVertices, createGoogleMapShapeFromCustomShape, createPreciseLine, createPreciseRectangle, createShapeHolderFromCustomShape, getShapeIconName, googleShapeToGeoJsonData, hideShape, makeShapeDraggable, makeShapeEditable, metersToMiles, milesToMeters, removeShapeFromMap, setShapeOptions, showShape, toMeters } from '../helpers/shapes';
import { customElement, property, query, state } from 'lit/decorators';
import { intoPointRequest, isDefined, testJSON, undefinedIfEmpty } from '../helpers/utils';

import { CategorySelector } from './theme/category-selector';
import { ColorPicker } from './color-picker';
import { FormTextarea } from './theme/form-textarea';
import { ProxiTextfield } from './theme/proxi-textfield';
import { RangeSliderComponent } from './range-slider-component';
import { StateController } from '../helpers/state/state-management/state-controller';
import center from '@turf/center';
import drawingTool from '../helpers/state/drawing-tool';
import featuresInfo from '../helpers/state/features-info';
import { geometry } from '@turf/helpers';
import { getMainPage } from '../helpers/page-name';
import { hexToRgba } from '../helpers/colors';
import { palette } from '../style/variables';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

@customElement('edit-shape-modal')
export class EditShapeModal extends LitElement {
    private readonly amplitude_callsite: string = 'Edit Shape Modal';

    @query('#shapeTitle')
    private readonly nameElement?: ProxiTextfield | null;

    @query('#shapeDescription')
    private readonly descriptionElement?: ProxiTextfield| null;

    @query('#codeText')
    private readonly codeElement!: FormTextarea;

    @query('#strokeColor')
    private readonly strokeColorElement!: ColorPicker;

    @query('#fillColor')
    private readonly fillColorElement!: ColorPicker;

    @query('multi-category-selector')
    private readonly categorySelectorElement!: CategorySelector;

    @property({ type: String })
    type: string = '';

    @property()
    code: string = '';

    @property({ type: Boolean })
    geoJsonOnlyMode: boolean = false;

    @property({ type: Boolean })
    isAddEditPointMode: boolean = false;

    @state()
    errorMessage: string = '';

    @query('#strokeWidthRangeSlider')
    strokeWidthRangeSlider!: RangeSliderComponent;

    @query('#strokeOpacityRangeSlider')
    strokeOpacityRangeSlider!: RangeSliderComponent;

    @query('#fillOpacityRangeSlider')
    fillOpacityRangeSlider!: RangeSliderComponent;

    @query('#radiusRangeSlider')
    radiusRangeSlider!: ProxiTextfield;

    @state()
    focusedShape?: TopLevelShapeHolder;

    @state()
    selectedTab: string = 'parameters';

    @state()
    hasEntitlement: boolean = true;

    @state()
    focusedShapeChanged: boolean = false;

    @state()
    circle: google.maps.Circle | undefined;

    @state()
    drawNewShape: boolean = false;

    @state()
    showSelectShapeType: boolean = false;

    @state()
    radius_unit: string = 'meters';

    @state()
    selectedCategory: string | undefined;

    @state()
    geoJsonShapes: TopLevelShapeHolder[] = [];

    @state()
    preciseMode: 'rectangle' | 'line' | null = null;

    @state()
    preciseUnit: PreciseShapeUnit = 'feet';

    @state()
    preciseWidth: string = '';

    @state()
    preciseHeight: string = '';

    @state()
    preciseLength: string = '';

    @state()
    preciseBearing: string = '0';

    @state()
    waitingForMapClick: boolean = false;

    private mapClickListener?: google.maps.MapsEventListener;

    @state()
    editDefaultSettingsMode: boolean = false;

    private readonly bindDrawingToolState = new StateController(this, drawingTool);
    private readonly bindTopicDataState = new StateController(this, topicData);
    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindFeaturesInfoState = new StateController(this, featuresInfo);

    protected async firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): Promise<void> {
        try {
            await this.strokeColorElement?.updateLayout();
        } catch (e) {
        }
        try {
            await this.fillColorElement?.updateLayout();
        } catch (e) {
        }
        this.hasEntitlement = (featuresInfo.customShapesEnabled || (topicData.topic?.custom_shapes?.length ?? 0) < 3) && pointCache.pointsRemaining > 0;
    }

    async updated(changedProperties: any) {

        if (changedProperties.has('focusedShape') === true && isDefined(this.focusedShape) && !this.geoJsonOnlyMode) {
            if (isDefined(this.focusedShape) && isDefined(this.focusedShape.inner))
                this.type = this.focusedShape.shape.geojson.type;


                this.code = JSON.stringify({
                    type: 'Feature',
                    properties: {
                        name: this.focusedShape.shape.name ?? '',
                        description: this.focusedShape.shape.description ?? '',
                        stroke_color: this.focusedShape.shape.style?.stroke_color ?? topicData.topic?.default_shape_style?.stroke_color ?? '#000000',
                        stroke_weight: this.focusedShape.shape.style?.stroke_weight ?? topicData.topic?.default_shape_style?.stroke_weight ?? 2,
                        stroke_opacity: this.focusedShape.shape.style?.stroke_opacity ?? topicData.topic?.default_shape_style?.stroke_opacity ?? 0.5,
                        fill_color: this.focusedShape.shape.style?.fill_color ?? topicData.topic?.default_shape_style?.fill_color ?? '#000000',
                        fill_opacity: this.focusedShape.shape.style?.fill_opacity ?? topicData.topic?.default_shape_style?.fill_opacity ?? 0.5,
                    },
                    geometry: this.focusedShape.shape.geojson,
                }, null, 2);


                // set feature as editable
                if (!this.focusedShape.shape.circle_details && isDefined(this.focusedShape.inner)) {
                    if (countVertices(this.focusedShape?.shape?.geojson?.coordinates ?? []) < 100 && this.focusedShape.shape.geojson.type !== 'GeometryCollection') {
                        makeShapeEditable(this.focusedShape.inner, true);
                        makeShapeDraggable(this.focusedShape.inner, true);
                        // listen to changes
                        // Check if the feature is a Polygon or Polyline before adding the listener
                        if (this.focusedShape.inner instanceof google.maps.Polygon || this.focusedShape.inner instanceof google.maps.Polyline) {
                            google.maps.event.addListener(this.focusedShape.inner.getPath(), 'set_at', this.updateShapeDetails);

                            // Listen for 'insert_at' event
                            google.maps.event.addListener(this.focusedShape.inner.getPath(), 'insert_at', this.updateShapeDetails);

                            // Listen for 'remove_at' event
                            google.maps.event.addListener(this.focusedShape.inner.getPath(), 'remove_at', this.updateShapeDetails);
                        } else {
                            console.warn('Feature is not a Polygon or Polyline, cannot add path listener');
                        }
                    }

                    addShapeToMap(this.focusedShape.inner, topicData.mapComponent?.googleMapComponent.map);
                } else {
                    if (!isDefined(this.circle) && isDefined(this.focusedShape.shape.circle_details)) {
                        hideShape(this.focusedShape.inner);
                        this.circle = new google.maps.Circle({
                            center: this.focusedShape.shape.circle_details.center,
                            radius: this.focusedShape.shape.circle_details.radius_m,
                            editable: true,
                            draggable: true,
                            strokeColor: this.focusedShape.shape.style?.stroke_color,
                            strokeWeight: this.focusedShape.shape.style?.stroke_weight,
                            strokeOpacity: this.focusedShape.shape.style?.stroke_opacity,
                            fillColor: this.focusedShape.shape.style?.fill_color,
                            fillOpacity: this.focusedShape.shape.style?.fill_opacity,
                            map: topicData.mapComponent?.googleMapComponent.map,
                        });

                        this.focusedShapeChanged = true;
                        google.maps.event.addListener(this.circle, 'radius_changed', this.updateShapeDetails);
                        google.maps.event.addListener(this.circle, 'center_changed', this.updateShapeDetails);
                        google.maps.event.addListener(this.circle, 'dragend', this.updateShapeDetails
                        );
                    }
            }
        }
    }

    updateShapeDetails = () => {
        this.focusedShapeChanged = true;
        if (this.focusedShape) {
            const details = googleShapeToGeoJsonData(this.circle ?? this.focusedShape.inner, this.circle ? 'circle' : this.type);

            this.focusedShape.shape.geojson = details.geometry;
            this.focusedShape.shape.circle_details = details.circle_details;


            if (this.circle) {
                const newGoogleObject = createGoogleMapShapeFromCustomShape(this.focusedShape.shape);
                if (newGoogleObject instanceof google.maps.Polygon && this.focusedShape.inner instanceof google.maps.Polygon) {
                    hideShape(newGoogleObject);
                    removeShapeFromMap(newGoogleObject);
                    this.focusedShape.inner?.setPath(newGoogleObject.getPath());
                    hideShape(this.focusedShape.inner);
                }
            }
        }
        if (this.circle && isDefined(this.radiusRangeSlider)) {
            this.radiusRangeSlider.value = this.radiusText?.toString() ?? '0';
        }
    };


    handleChangeTab(tab: string) {
        if (this.selectedTab === 'parameters' && this.focusedShape) {
            this.focusedShape.shape.name = this.nameElement?.value;
            this.focusedShape.shape.description = this.descriptionElement?.value;
            this.focusedShape.shape.style = {
                stroke_color: this.strokeColorElement?.value,
                stroke_weight: this.strokeWidthRangeSlider?.value,
                stroke_opacity: (this.strokeOpacityRangeSlider?.value ?? 100) / 100,
                fill_color: this.fillColorElement?.value,
                fill_opacity: (this.fillOpacityRangeSlider?.value ?? 100) / 100,
            };
        }
        this.selectedTab = tab;
    }

    async openUpgradeDialog() {
        this.dispatchEvent(new CustomEvent('open-upgrade-dialog'));
        await this.reset();
    }

    handleChangeCode(event: any) {
        this.errorMessage = '';
        this.requestUpdate();
    }

    async onSaveChanges() {
        if (!this.focusedShape) {
            return;
        }
        if (
            this.focusedShape.shape.geojson.type !== 'Point' || this.geoJsonOnlyMode
        ) {
            if (this.selectedTab === 'parameters' && !this.geoJsonOnlyMode) {
                    const customShapes = topicData.topic?.custom_shapes ?? [];
                    if (isDefined(this.focusedShape.id)) {
                        customShapes.forEach((shape: CustomShape) => {
                            if (shape._id?.$oid === this.focusedShape?.shape._id?.$oid && this.focusedShape) {
                                shape.name = this.nameElement?.value ?? this.focusedShape?.shape?.name ?? `New Shape ${customShapes.length + 1}`;
                                shape.description = this.descriptionElement?.value ?? this.focusedShape?.shape?.description ?? undefined;
                                shape.geojson = {
                                    type: this.focusedShape.shape.geojson.type,
                                    coordinates: this.focusedShape.shape.geojson.coordinates,
                                };
                                shape.style = {
                                    stroke_color: undefinedIfEmpty(this.strokeColorElement?.value),
                                    stroke_weight: this.strokeWidthRangeSlider.value ?? this.focusedShape?.shape?.style?.stroke_weight ?? undefined,
                                    stroke_opacity: (this.strokeOpacityRangeSlider.value ?? 100) / 100,
                                    fill_color: undefinedIfEmpty(this.fillColorElement?.value),
                                    fill_opacity: isDefined(this.fillOpacityRangeSlider) ? (this.fillOpacityRangeSlider.value ?? 100) / 100 : this.focusedShape?.shape?.style?.fill_opacity ?? undefined,
                                };
                                shape.circle_details = this.focusedShape.shape.circle_details;
                            }
                        });
                    } else {
                        customShapes.push({
                            name: this.nameElement?.value ?? this.focusedShape?.shape?.name ?? `New Shape ${customShapes.length + 1}`,
                            description: this.descriptionElement?.value ?? this.focusedShape?.shape?.description ?? undefined,
                            geojson: {
                                type: this.focusedShape.shape.geojson.type,
                                coordinates: this.focusedShape.shape.geojson.coordinates,
                            },
                            style: {
                                stroke_color: undefinedIfEmpty(this.strokeColorElement?.value) ?? this.focusedShape?.shape?.style?.stroke_color ?? undefined,
                                stroke_weight: this.strokeWidthRangeSlider.value ?? this.focusedShape?.shape?.style?.stroke_weight ?? undefined,
                                stroke_opacity: (this.strokeOpacityRangeSlider.value ?? 100) / 100,
                                fill_color: undefinedIfEmpty(this.fillColorElement?.value),
                                fill_opacity: isDefined(this.fillOpacityRangeSlider) ? (this.fillOpacityRangeSlider.value ?? 100) / 100 : this.focusedShape?.shape?.style?.fill_opacity ?? undefined,
                            },
                            circle_details: this.focusedShape.shape.circle_details,
                        });
                        // remove the preview shapes from the map
                        topicData.mapComponent?.googleMapComponent.map.data.forEach((feature: any) => {
                            if (feature.getProperty('_id') === 'preview') {
                                topicData.mapComponent?.googleMapComponent.map.data.remove(feature);
                            }
                        });
                    }
                    await topicData.fetchAndUpdateTopicData({custom_shapes: customShapes}, this.amplitude_callsite);
                    // add the new shape from topic data to the map
                    await this.reset(false);
            } else if (this.selectedTab === 'code' || this.geoJsonOnlyMode) {
                if (testJSON(this.codeElement?.value ?? this.codeElement?.placeholder ?? '')) {
                    await this.saveCodeAsBackground(this.codeElement?.value ?? this.codeElement?.placeholder ?? '');
                } else {
                    this.errorMessage = 'This is not valid JSON.';
                }
            }

        }
    }

    onPreviewGeoJson() {
        // topicData.mapComponent?.cleanupListeners();
        if (testJSON(this.codeElement?.value ?? '')) {
            // minify the json
            this.code = this.minifyJSON(this.codeElement?.value ?? '');
            if (isDefined(this.codeElement)) {
                this.codeElement.value = this.code;
            };

            if (this.code.length >= 500000) {
                getMainPage().errorToastElement.showToastAndThrow('The code is too large to preview');
                return;
            }
            const customShapes = codeToCustomShapes(this.codeElement?.value ?? '', []);
            const shapeHolders = customShapes.map((shape: CustomShape) => createShapeHolderFromCustomShape(shape));

            // remove any existing preview shapes
            this.geoJsonShapes.forEach((shape: TopLevelShapeHolder) => {
                removeShapeFromMap(shape.inner);
            } );

            // add new preview shapes
            this.geoJsonShapes = shapeHolders;
            this.geoJsonShapes.forEach((shape: TopLevelShapeHolder) => {
                addShapeToMap(shape.inner, topicData.mapComponent?.googleMapComponent.map);
            });

            this.requestUpdate();
        } else {
            this.errorMessage = 'This is not valid JSON.';
        }
    }

    async reset(close: boolean = true){
        this.cleanupMapClickListener();
        this.preciseMode = null;
        this.waitingForMapClick = false;
        if (topicData.mapComponent) {
            // remove the shape from the map
            if (isDefined(this.focusedShape) && isDefined(this.focusedShape?.id)) {
                makeShapeEditable(this.focusedShape.inner, false);
                makeShapeDraggable(this.focusedShape.inner, false);
                removeShapeFromMap(this.focusedShape.inner);
                drawingTool.removeShapeById(this.focusedShape?.id);
                this.showSelectShapeType = false;
            } else {
                if (this.focusedShape?.inner) {
                    makeShapeEditable(this.focusedShape.inner, false);
                    makeShapeDraggable(this.focusedShape.inner, false);
                    removeShapeFromMap(this.focusedShape.inner);
                    this.focusedShape.inner = undefined;
                    this.focusedShape.id = undefined;
                }
                this.showSelectShapeType = false;
                removeShapeFromMap(topicData.mapComponent.foucsedShapeHolder?.inner);
                topicData.mapComponent.foucsedShapeHolder = undefined;
                topicData.mapComponent._feature = undefined;
            }
            this.circle?.setEditable(false);
            this.circle?.setMap(null);
            this.circle = undefined;
            topicData.mapComponent.showDrawToolOptions = false;
            topicData.mapComponent.askForShapeType = false;

            await topicData.mapComponent?.loadShapesGeoJson();
        }
        this.hasEntitlement = (featuresInfo.customShapesEnabled || (topicData.topic?.custom_shapes?.length ?? 0) < 3) && pointCache.pointsRemaining > 0;
        if (close) {
            await topicData.mapComponent?.closeMapDrawer();
        } else {
            this.focusedShape = undefined;
            this.requestUpdate();
        }
    }

    async saveCodeAsOnePoint() {
        this.onPreviewGeoJson();
        const shape = this.geoJsonShapes;
        if (!isDefined(shape[0]) || !topicData.mapComponent) {
            return;
        }
        if (shape.length === 1) {
            const feature = shape[0];
            topicData.mapComponent.foucsedShapeHolder = feature;
            setShapeOptions(topicData.mapComponent?.foucsedShapeHolder.inner, {clickable: false});
            await topicData.mapComponent?.createPointFromShape();
        } else {
            // turn into a geometry collection
            const feature: CustomShape = {
                name: 'New Shape',
                description: '',
                geojson: {
                    type: GeoJsonType.GeometryCollection,
                    coordinates: shape.map((s: TopLevelShapeHolder) => s.shape.geojson),
                },
                style: {
                    stroke_color: '#000000',
                    stroke_weight: 2,
                    stroke_opacity: 0.5,
                    fill_color: '#000000',
                    fill_opacity: 0.5,
                },
            };

            topicData.mapComponent.foucsedShapeHolder = createShapeHolderFromCustomShape(feature);
            setShapeOptions(topicData.mapComponent?.foucsedShapeHolder.inner, {clickable: false});
            await topicData.mapComponent?.createPointFromShape();
        }
    }

    async saveCodeAsBackground(jsonCode: string) {
        const minifiedCode = this.minifyJSON(jsonCode);
        if (minifiedCode.length >= 500000) {
            getMainPage().errorToastElement.showToastAndThrow('The code is too large to save');
            return;
        }
        try {
            let customShapes = topicData.topic?.custom_shapes ?? [];
            customShapes = codeToCustomShapes(jsonCode, customShapes);

            await topicData.fetchAndUpdateTopicData({custom_shapes: customShapes}, this.amplitude_callsite);
            // remove the preview shapes from the map
            topicData.mapComponent?.googleMapComponent.map.data.forEach((feature: any) => {
                if (feature.getProperty('_id') === 'preview') {
                    topicData.mapComponent?.googleMapComponent.map.data.remove(feature);
                }
            });
            await this.reset(false);
            await topicData.mapComponent?.closeMapDrawer();
            topicData.mapComponent?.disableDrawMode();
        } catch (err) {
            getMainPage().errorToastElement.showToastAndThrow('Error saving shape');
        }
    }

    // minify json to save space
    minifyJSON(json: string) {
        return json.replace(/[\s\n]/g, '');
    }

    async onDeleteShape(shape?: TopLevelShapeHolder) {
        if (!shape) {
            return;
        }

        // confirm if the user wants to delete the shape
        if (!confirm('Are you sure you want to delete this shape? This can not be undone.')) {
            return;
        }
        if (shape?.id) {
            drawingTool.removeShapeById(shape.id);
            const customShapes = topicData.topic?.custom_shapes ?? [];
            const newShapes = customShapes.filter((cs_shape: CustomShape) => cs_shape._id?.$oid !== shape?.id);
            await topicData.fetchAndUpdateTopicData({custom_shapes: newShapes}, this.amplitude_callsite);
            await this.reset(false);
        } else {
            await this.reset(false);
        };
    }

    async onStylingChange(field: string, value: any) {
        if (this.editDefaultSettingsMode) {
            await this.onEditStyleDefaultChange(field, value);
        } else {
            this.onShapeStylingChange(field, value);
        }
    }

    async onEditStyleDefaultChange(field: string, value: any) {
        if (!topicData.topic) {
            return;
        }
        if (!topicData.topic.default_shape_style) {
            topicData.topic.default_shape_style = {
                stroke_color: '#000000',
                stroke_weight: 2,
                stroke_opacity: 0.5,
                fill_color: '#000000',
                fill_opacity: 0.5,
            };
        }

        (topicData.topic.default_shape_style as { [key: string]: any })[field] = value;
        const options: GeoJsonStyle = topicData.topic.default_shape_style;

        await topicData.fetchAndUpdateTopicData({default_shape_style: options}, this.amplitude_callsite);
    }

    onShapeStylingChange(field: string, value: any) {
        if (!this.focusedShape?.shape.style && this.focusedShape) {
            this.focusedShape.shape.style = topicData.topic?.default_shape_style ?? {
                stroke_color: '#000000',
                stroke_weight: 2,
                stroke_opacity: 0.5,
                fill_color: '#000000',
                fill_opacity: 0.5
            };
        }

        (this.focusedShape?.shape.style as { [key: string]: any })[field] = value;
        const feature = this.focusedShape?.inner;
        if (!feature) {
            console.warn('No feature to update');
            return;
        }
        const options = {
            strokeColor: this.focusedShape?.shape?.style?.stroke_color,
            strokeWeight: this.focusedShape?.shape?.style?.stroke_weight,
            strokeOpacity: this.focusedShape?.shape?.style?.stroke_opacity,
            fillColor: this.focusedShape?.shape?.style?.fill_color,
            fillOpacity: this.focusedShape?.shape?.style?.fill_opacity,
        };
        setShapeOptions(feature, options);

        if (this.circle) {
                this.circle.setOptions({
                    strokeColor: this.focusedShape?.shape?.style?.stroke_color,
                    strokeWeight: this.focusedShape?.shape?.style?.stroke_weight,
                    strokeOpacity: this.focusedShape?.shape?.style?.stroke_opacity,
                    fillColor: this.focusedShape?.shape?.style?.fill_color,
                    fillOpacity: this.focusedShape?.shape?.style?.fill_opacity,
                });
            }
    }

    onNameChange() {
        if (this.focusedShape) {
            this.focusedShape.shape.name = this.nameElement?.value;
        }
    }

    onDescriptionChange() {
        if (this.focusedShape) {
            this.focusedShape.shape.description = this.descriptionElement?.value;
        }
    }

    async createPointFromShape() {
        if (!this.focusedShape) {
            return;
        }

        const feature = this.focusedShape;
        if (topicData.mapComponent) {
            // create a new point from the shape
            const newShape = createShapeHolderFromCustomShape(feature.shape);
            topicData.mapComponent.foucsedShapeHolder = newShape;
            addShapeToMap(newShape.inner, topicData.mapComponent.googleMapComponent.map);
            setShapeOptions(newShape.inner, {clickable: false});
            await topicData.mapComponent.createPointFromShape();
        }

        // delete this shape
        await this.onDeleteShape(this.focusedShape);
    }

    updateCircleRadius() {
        // check that radius is a number
        if (isNaN(Number(this.radiusRangeSlider.value))) {
            this.radiusRangeSlider.value = (this.focusedShape?.shape.circle_details?.radius_m ?? 0).toString();
            return;
        }

        // if miles, convert to meters

        let radius = Number(this.radiusRangeSlider.value);
        if (this.radius_unit === 'miles') {
            radius = milesToMeters(radius);
        }
        if (this.circle) {
            this.circle.setRadius(radius);
            if (this.focusedShape?.shape.circle_details) {
                this.focusedShape.shape.circle_details.radius_m = radius;
            }
        }
    }

    _unitChangeHandler(event: { target: { value: any; }; }) {
        this.radius_unit = event.target.value;
    }

    get radiusText() {
        if (!this.focusedShape?.shape.circle_details) {
            return 0;
        }
        return (this.radius_unit === 'miles' ? metersToMiles(this.circle?.getRadius() ?? 0) : this.circle?.getRadius())?.toFixed(3);
    }

    render() {
        if (this.geoJsonOnlyMode) {
            return this.renderCode();
        } else if (this.editDefaultSettingsMode) {
            return this.renderDefaultStyleEditor();
        } else if (this.preciseMode) {
            return this.renderPreciseForm();
        } else if (this.showSelectShapeType) {
            return this.renderSelectType();
        } else if (this.focusedShape === undefined) {
            return this.renderListOfShapes();
        } else if (this.isAddEditPointMode) {
            return this.renderPointShapeEditor();
        }
        else {
            return html`
                <div class="edit-polygon-modal__wrapper">
                    <div class="style-group">
                        <div class="edit-mode__tabs">
                            <div
                                class="edit-mode__tab left ${this.selectedTab === 'parameters'
                                    ? 'active'
                                    : ''}"
                                @click="${() => this.handleChangeTab('parameters')}"
                            >
                                Edit Parameters
                            </div>
                            <div
                                class="edit-mode__tab right ${this.selectedTab === 'code'
                                    ? 'active'
                                    : ''}"
                                @click="${() => this.handleChangeTab('code')}"
                            >
                                Edit Code
                            </div>
                        </div>

                        ${this.renderStyleControls()}
                    </div>
                    <div class="buttonGroup">
                        ${this.hasEntitlement ? '' : html` <span>Note: Your shapes will not appear on your maps until you upgrade to Proxi Pro</span>`}
                        <div class="buttonArea">
                            ${this.hasEntitlement && isDefined(this.focusedShape) ? html`
                                <form-button size="md" variant="outline" class="button red" slot="secondaryAction" raised @click=${async () => await this.onDeleteShape(this.focusedShape)}>${this.focusedShape.id ? 'Delete' : 'Cancel'}</form-button>
                                <form-button size="md" class="button green" slot="primaryAction" raised @click=${this.onSaveChanges}>Save</form-button>
                            ` : html`
                                <form-button size="md" class="button" raised @click=${this.openUpgradeDialog}>Upgrade to Add More Shapes</form-button>
                            `}
                        </div>
                        ${this.focusedShape?.shape._id && this.hasEntitlement && isDefined(this.focusedShape) ? html`
                            <span class="changeToData" @click=${this.createPointFromShape}>Change to Data Point</span>
                        ` : ''}
                    </div>
                </div>
            `;
        }
    }

    renderTitleAndDescription(typeText: string) {
        return html`
            <div class="form-control">
                <proxi-textfield
                    outlined
                    id="shapeTitle"
                    type="text"
                    ?required=${true}
                    @change=${this.onNameChange}
                    label="${typeText} Title"
                    .value=${this.focusedShape?.shape?.name as string ?? ''}
                    placeholder="E.g “Polygon #1” or “area affected by the fire”."
                    maxLength="80">
                </proxi-textfield>
            </div>
            <div class="form-control">
                <proxi-textfield
                    outlined
                    id="shapeDescription"
                    type="text"
                    ?required=${false}
                    label="${typeText} Description"
                    @change=${this.onDescriptionChange}
                    .value=${this.focusedShape?.shape?.description as string ?? ''}
                    placeholder="Add a description">
                </proxi-textfield>
            </div>
        `;
    }

    renderStyleControls(showTitleAndDescription: boolean = true) {
        const typeText = this.circle ? 'Circle' : this.type.includes('line')? 'Line' : this.type === 'MultiLineString' ? 'Line' : this.type.includes('polygon') ? 'Polygon' : 'Shape';
        return html`
            ${this.selectedTab === 'parameters' ? html`
                ${showTitleAndDescription ? html`
                    ${this.renderTitleAndDescription(typeText)}
                ` : ''}
                ${this.focusedShape?.shape?.circle_details && this.circle ? html`
                    <div style="display: flex; width: 100%; justify-content: flex-end;">
                        <proxi-selectfield-inline  id="unitSelect" @change=${this._unitChangeHandler} value=${this.radius_unit}>
                            <mwc-list-item value="meters" ?selected=${this.radius_unit === 'meters'}>Meters</mwc-list-item>
                            <mwc-list-item value="miles" ?selected=${this.radius_unit === 'miles'}>Miles</mwc-list-item>
                        </proxi-selectfield-inline>
                    </div>
                    <proxi-textfield
                        outlined
                        id="radiusRangeSlider"
                        type="number"
                        label="Circle Radius"
                        .value=${this.radiusText?.toString() ?? '0'}
                        @change=${this.updateCircleRadius}
                        placeholder="Radius"
                        .suffix="${this.radius_unit}"
                        maxLength="10">
                        ${this.radius_unit === 'miles' ? html`<span>${metersToMiles(this.focusedShape?.shape?.circle_details.radius_m )} miles</span>` : ''}
                    </proxi-textfield>
                    <br /><br />
                ` : ''}
                <color-picker
                    id="strokeColor"
                    class="input"
                    .open=${true}
                    @change=${async () => await this.onStylingChange('stroke_color', this.strokeColorElement.value)}
                    .value="${this.focusedShape?.shape?.style?.stroke_color as string ?? topicData.topic?.default_shape_style?.stroke_color ?? '#000000'}"
                    label="Stroke Color">
                </color-picker><br />
                <range-slider-component
                    id="strokeWidthRangeSlider"
                    minString="Stroke Width"
                    maxString=""
                    min=${1}
                    max=${10}
                    step=${1}
                    unit=${''}
                    @change=${async () => await this.onStylingChange('stroke_weight', this.strokeWidthRangeSlider.value)}
                    .value=${this.focusedShape?.shape?.style?.stroke_weight as number ?? topicData.topic?.default_shape_style?.stroke_weight ?? 2}
                ></range-slider-component>
                <range-slider-component
                    id="strokeOpacityRangeSlider"
                    minString="Stroke Opacity"
                    maxString=""
                    min=${0}
                    max=${100}
                    step=${1}
                    unit=${'%'}
                    @change=${async () => await this.onStylingChange('stroke_opacity', (this.strokeOpacityRangeSlider.value ?? 100) / 100)}
                    .value=${(this.focusedShape?.shape?.style?.stroke_opacity as number ?? topicData.topic?.default_shape_style?.stroke_opacity ?? 0.5) * 100}
                ></range-slider-component>
                ${this.type !== 'LineString' && this.type !== 'MultiLineString' ? html`
                    <color-picker
                        id="fillColor"
                        class="input"
                        .open=${true}
                        @change=${async () => await this.onStylingChange('fill_color', this.fillColorElement.value)}
                        value="${this.focusedShape?.shape?.style?.fill_color as string ?? topicData.topic?.default_shape_style?.fill_color ?? '#000000'}"
                        label="Fill Color">
                    </color-picker><br />
                ` : ''}

                ${this.type !== 'LineString' && this.type !== 'MultiLineString' ? html`
                    <range-slider-component
                        id="fillOpacityRangeSlider"
                        minString="Fill Opacity"
                        maxString=""
                        min=${0}
                        max=${100}
                        step=${1}
                        unit=${'%'}
                        @change=${async () => await this.onStylingChange('fill_opacity', (this.fillOpacityRangeSlider.value ?? 100) / 100)}
                        .value=${(this.focusedShape?.shape?.style?.fill_opacity as number ?? topicData.topic?.default_shape_style?.fill_opacity ?? 0.5) * 100}
                    ></range-slider-component>
                ` : ''}
            ` : ''}

            ${this.selectedTab === 'code' ? html`
                <div class="codeWrapper">
                    <form-textarea
                        id="codeText"
                        type="text"
                        ?required=${false}
                        .value="${this.code}"
                        placeholder="Features Code"
                        hideMaxLength
                        @change=${this.handleChangeCode}
                    >
                    </form-textarea>
                    <div class="error-message">
                        ${this.errorMessage}
                    </div>
                </div>
            ` : ''}
        `;
    }

    renderPointShapeEditor() {
        topicData.mapComponent?.googleMapComponent.map.data.forEach((feature: any) => {
                if (feature.getProperty('_id') === 'preview') {
                    topicData.mapComponent?.googleMapComponent.map.data.remove(feature);
                }
            });
        return html`
        ${this.renderStyleControls(false)}
        `;
    }

    renderDefaultStyleEditor() {
        return html`
            <h4 class="edit-polygon-modal__title">Edit Default Shape Style</h4>
            <p class="edit-polygon-modal__description">Edit the default style for all shapes on the map.</p>

            <div class="style-group">
                ${this.renderStyleControls(false)}
            </div>
            <div class="buttonGroup" style="display: flex; justify-content: space-between; flex-direction: row;">
                <form-button variant="outline" size="md" raised @click=${() => {this.editDefaultSettingsMode = false;}}>
                    Back
                </form-button>
                ${this.numberOfShapes > 0 ? html`
                    <form-button size="md" @click=${this.updateAllShapeStyles}>Apply to All Shapes</form-button>
                ` : ''}
            </div>
        `;
    }

    stylesOpen: boolean = false;

    openMultiPoints() {
        this.stylesOpen = true;
        this.selectedTab = 'parameters';
        const temp_grouped_shape = {
            shape: {
                name: 'New Shape',
                description: '',
                geojson: {
                    type: GeoJsonType.GeometryCollection,
                    coordinates: this.geoJsonShapes.map((s: TopLevelShapeHolder) => s.shape.geojson),
                },
                style: topicData.topic?.default_shape_style ?? {
                    stroke_color: '#000000',
                    stroke_weight: 2,
                    stroke_opacity: 0.5,
                    fill_color: '#000000',
                    fill_opacity: 0.5,
                },
            },
        };

        this.focusedShape = createShapeHolderFromCustomShape(temp_grouped_shape.shape);
        addShapeToMap(this.focusedShape.inner, topicData.mapComponent?.googleMapComponent.map);
        showShape(this.focusedShape.inner);

        this.geoJsonShapes.forEach((shape: TopLevelShapeHolder) => {
            hideShape(shape.inner);
        } );

        this.requestUpdate();
    }

    async addIndividualPointsToMap() {
        const new_points:Array< CreatePointRequest | undefined> = this.geoJsonShapes.map((shape: TopLevelShapeHolder) => {
            const to_be_centered = {
                type: GeoJsonType[shape.shape.geojson.type as keyof typeof GeoJsonType],
                coordinates: shape.shape.geojson.coordinates,
            };
            let newLocation: CreateLocationRequest | undefined;
            if (to_be_centered.coordinates !== undefined && shape.shape.geojson.type !== 'GeometryCollection') {
                const refined_geometry = geometry(shape.shape.geojson.type, to_be_centered.coordinates);
                const polygon = center(refined_geometry);

                newLocation  = {
                    search: 'Shape',
                    place_id: 'shape',
                    latLng: {
                        lat: polygon.geometry.coordinates[1] ?? 0,
                        lng: polygon.geometry.coordinates[0] ?? 0
                    }
                };
            } else if (shape.shape.geojson.type === 'GeometryCollection') {
                newLocation = {
                    search: 'Shape',
                    place_id: 'shape',
                    latLng: {
                        lat: topicData.mapComponent?.googleMapComponent?.map?.getCenter().lat() ?? 0,
                        lng: topicData.mapComponent?.googleMapComponent?.map?.getCenter().lng() ?? 0
                    }
                };
            }

            const category_id = this.selectedCategory ?? this.categorySelectorElement?.value ?? topicData.categories[0]?._id?.$oid ?? ZERO_OBJECT_ID;
            if (topicData.topic?._id === undefined || newLocation === undefined) {
                getMainPage().errorToastElement.showToastAndThrow('Error saving shape');
                return undefined;
            }

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const category: ObjectId = { $oid: category_id } as ObjectId;

            shape.shape.style = this.focusedShape?.shape.style;

            return {
                name: shape.shape.name,
                custom_shape: shape.shape,
                topic_id: topicData.topic?._id,
                _id: undefined,
                topic_category_id: category,
                tag_info: {
                    user_tags: [],
                },
                email: undefined,
                special_offer: undefined,
                embedded_video: undefined,
                url: undefined,
                active: true,
                featured: false,
                custom_icon_url: undefined,
                description: shape.shape.description,
                start: undefined,
                end: undefined,
                number: undefined,
                custom_information: undefined,
                is_gate_hidden: false,
                source: undefined,
                location: newLocation

            };
        } );

        // remove undefined values
        const new_points_filtered = new_points.filter((point) => point !== undefined) as CreatePointRequest[];

        await pointCache.fetchAndAddMany(new_points_filtered);

        // remove the preview shapes from the map
        removeShapeFromMap(this.focusedShape?.inner);
        await this.reset(true);
    }

    renderCode() {
        return html`
            <div class="edit-line-modal__wrapper" style="height: 100%;overflow-y: auto;">
                <p>Note: When adding shapes with GeoJson, the shapes can occasionally duplicate after save. Try refreshing the page after saving your shapes to the map. </p>
                <div class="codeWrapper">
                    <form-textarea
                        id="codeText"
                        type="text"
                        ?required=${false}
                        hideMaxLength
                        @change=${this.handleChangeCode}
                    >
                    </form-textarea>
                    <div class="error-message">
                        ${this.errorMessage}
                    </div>
                </div>
                <p> Found ${this.geoJsonShapes.length} ${this.geoJsonShapes.length === 1 ? 'shape' : 'shapes'} in the code.</p>
                <div class="buttonGroup">
                    ${this.hasEntitlement ? html`
                        <form-button size="md" variant="outline" class="button red" slot="secondaryAction" raised @click=${this.onPreviewGeoJson}>Preview</form-button>
                        ${this.geoJsonShapes.length >= 1 ? html`
                            <form-button size="md" slot="primaryAction" raised @click=${async () => await this.saveCodeAsBackground(this.code)}>Save as Background Shape</form-button>
                            <form-button size="md" slot="primaryAction" raised @click=${this.saveCodeAsOnePoint} ?disabled=${this.geoJsonShapes.length === 0}>
                                    Save ${this.geoJsonShapes.length > 1 ? 'All Shapes' : 'Shape'} as ${this.geoJsonShapes.length > 1 ? 'One' : ''} Data Point
                                </form-button>
                            ${this.geoJsonShapes.length > 1 ? html`
                                ${!this.stylesOpen ? html`
                                    <form-button size="md" slot="primaryAction" raised
                                        ?disabled=${pointCache.pointsRemaining < this.geoJsonShapes.length || this.geoJsonShapes.length === 0}
                                        @click=${pointCache.pointsRemaining < this.geoJsonShapes.length ? this.openUpgradeDialog : this.openMultiPoints}>
                                        Save Shapes as ${this.geoJsonShapes.length} Separate Data Points
                                    </form-button>
                                    `: html`
                                        <category-selector id="multi-category-selector"
                                            .previousValue=${this.selectedCategory ?? topicData.categories[0]?._id?.$oid ?? ZERO_OBJECT_ID}
                                            @select=${(e: CustomEvent) => {this.selectedCategory = e.detail;}}
                                            ></category-selector>
                                        ${this.renderStyleControls(false)}
                                        <form-button size="md" slot="primaryAction" raised @click=${this.addIndividualPointsToMap}>
                                            Save ${this.geoJsonShapes.length} Data Points to Map</form-button>
                                            <p>This operation may take a few seconds. Please only click save once.</p>
                                    `}
                            ` : ''}
                        ` : ''}
                    ` : html`
                        <form-button size="md" variant="outline"  slot="secondaryAction" raised @click=${this.onPreviewGeoJson}>Preview</form-button>
                        <form-button size="md" raised @click=${this.openUpgradeDialog}>Upgrade to Add More Shapes</form-button>
                    `}
                </div>
            </div>
        `;
    }

    get numberOfShapes() {
        return (topicData.topic?.custom_shapes?.length ?? 0) + pointCache.allPoints.filter(point => point.custom_shape).length;
    }

    async updateAllShapeStyles() {
        if (confirm(`Are you sure you want to update all ${this.numberOfShapes} shapes with the new default style?`)) {
            const customShapes = topicData.topic?.custom_shapes ?? [];
            customShapes.forEach((shape: CustomShape) => {
                shape.style = topicData.topic?.default_shape_style;
            });
            await topicData.fetchAndUpdateTopicData({custom_shapes: customShapes}, this.amplitude_callsite);

            const updated_points = pointCache.allPoints.filter(point => point.custom_shape);
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            updated_points.forEach(async (point: GetPointResponse) => {
                if (point.custom_shape) {
                    point.custom_shape.style = topicData.topic?.default_shape_style;
                    await pointCache.modifyAndFetch(point._id?.$oid, intoPointRequest(point), true);
                } else {
                    console.warn('Point does not have custom shape');
                }
            }
            );

            // reload the page
            window.location.reload();
        }

    }

    async onEditShape(shapeHolder: TopLevelShapeHolder) {
        this.onShapeHoverEnd(shapeHolder);
        this.drawNewShape = false;
        await this.reset(false);
        this.focusedShape = shapeHolder;
    }

    async onDuplicateShape(source: TopLevelShapeHolder) {
        const duplicate: CustomShape = {
            name: `${source.shape.name ?? 'Shape'} (Copy)`,
            description: source.shape.description,
            geojson: {
                type: source.shape.geojson.type,
                coordinates: source.shape.geojson.coordinates,
            },
            style: {
                stroke_color: source.shape.style?.stroke_color,
                stroke_weight: source.shape.style?.stroke_weight,
                stroke_opacity: source.shape.style?.stroke_opacity,
                fill_color: source.shape.style?.fill_color,
                fill_opacity: source.shape.style?.fill_opacity,
            },
            circle_details: source.shape.circle_details,
            _id: undefined,   // <-- no _id means save() will insert, not update
        };

        const duplicateHolder = createShapeHolderFromCustomShape(duplicate);
        addShapeToMap(duplicateHolder.inner, topicData.mapComponent?.googleMapComponent.map);
        this.drawNewShape = false;
        await this.reset(false);
        this.focusedShape = duplicateHolder;
    }

    async drawNewLine() {
        await topicData.mapComponent?.startDrawing(google.maps.drawing.OverlayType.POLYLINE);

    }

    async drawNewPolygon() {
        await topicData.mapComponent?.startDrawing(google.maps.drawing.OverlayType.POLYGON);
    }

    async drawNewCircle() {
        await topicData.mapComponent?.startDrawing(google.maps.drawing.OverlayType.CIRCLE);
    }

    async addShapeGeojson() {
        await topicData.mapComponent?.setMapDrawer(MapDrawer.EditGeoJson);
    }

    async addRoute() {
        await topicData.mapComponent?.onOpenCreateRoute();
    }

    openPreciseRectangle() {
        this.preciseMode = 'rectangle';
        this.preciseWidth = '';
        this.preciseHeight = '';
        this.waitingForMapClick = false;
    }

    openPreciseLine() {
        this.preciseMode = 'line';
        this.preciseLength = '';
        this.preciseBearing = '0';
        this.waitingForMapClick = false;
    }

    cancelPreciseMode() {
        this.cleanupMapClickListener();
        this.preciseMode = null;
        this.waitingForMapClick = false;
        const map = topicData.mapComponent?.googleMapComponent.map as google.maps.Map | undefined;
        if (isDefined(map)) {
            map.setOptions({ draggableCursor: '' });
        }
    }

    private cleanupMapClickListener() {
        if (this.mapClickListener) {
            google.maps.event.removeListener(this.mapClickListener);
            this.mapClickListener = undefined;
        }
    }

    startPlacement() {
        if (this.preciseMode === 'rectangle') {
            const w = Number(this.preciseWidth);
            const h = Number(this.preciseHeight);
            if (!w || !h || w <= 0 || h <= 0) {
                getMainPage().errorToastElement.showToast('Please enter valid width and height values.');
                return;
            }
        } else if (this.preciseMode === 'line') {
            const len = Number(this.preciseLength);
            if (!len || len <= 0) {
                getMainPage().errorToastElement.showToast('Please enter a valid length.');
                return;
            }
        }

        const map = topicData.mapComponent?.googleMapComponent.map as google.maps.Map | undefined;
        if (!isDefined(map)) return;

        this.waitingForMapClick = true;
        map.setOptions({ draggableCursor: 'crosshair' });

        this.cleanupMapClickListener();
        this.mapClickListener = map.addListener('click', (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return;
            this.onMapClickForPreciseShape(e.latLng);
        });
    }

    private onMapClickForPreciseShape(latLng: google.maps.LatLng) {
        const center: [number, number] = [latLng.lng(), latLng.lat()];
        let geojson;

        if (this.preciseMode === 'rectangle') {
            const widthM = toMeters(Number(this.preciseWidth), this.preciseUnit);
            const heightM = toMeters(Number(this.preciseHeight), this.preciseUnit);
            geojson = createPreciseRectangle(center, widthM, heightM);
        } else if (this.preciseMode === 'line') {
            const lengthM = toMeters(Number(this.preciseLength), this.preciseUnit);
            const bearing = Number(this.preciseBearing) || 0;
            geojson = createPreciseLine(center, lengthM, bearing);
        }

        if (!geojson) return;

        this.cleanupMapClickListener();
        this.waitingForMapClick = false;
        const map = topicData.mapComponent?.googleMapComponent.map as google.maps.Map | undefined;
        if (!isDefined(map)) return;

        map.setOptions({ draggableCursor: '' });

        const shape: CustomShape = {
            name: this.preciseMode === 'rectangle'
                ? `${this.preciseWidth}x${this.preciseHeight} ${this.preciseUnit} rectangle`
                : `${this.preciseLength} ${this.preciseUnit} line`,
            geojson,
            style: topicData.topic?.default_shape_style ?? {
                stroke_color: '#000000',
                stroke_weight: 2,
                stroke_opacity: 1,
                fill_color: '#000000',
                fill_opacity: 0.5,
            },
        };

        const shapeHolder = createShapeHolderFromCustomShape(shape);
        addShapeToMap(shapeHolder.inner, map);

        this.preciseMode = null;
        this.focusedShape = shapeHolder;
    }

    renderPreciseForm() {
        const isRectangle = this.preciseMode === 'rectangle';
        return html`
            <div class="modalContent">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                    <h4>${isRectangle ? 'Precise Rectangle' : 'Precise Line'}</h4>
                    <a @click="${this.cancelPreciseMode}" style="cursor: pointer; text-decoration: underline;">Back</a>
                </div>
                ${this.waitingForMapClick ? html`
                    <div class="precise-placement-message">
                        <mwc-icon>my_location</mwc-icon>
                        <p>Click on the map to place the ${isRectangle ? 'center of the rectangle' : 'start of the line'}.</p>
                        <form-button size="sm" variant="outline" @click="${this.cancelPreciseMode}">Cancel</form-button>
                    </div>
                ` : html`
                    <div class="precise-form">
                        <div class="precise-form__row">
                            <label>Unit</label>
                            <proxi-selectfield-inline
                                id="preciseUnitSelect"
                                @change=${(e: any) => { this.preciseUnit = e.target.value; }}
                                value=${this.preciseUnit}
                            >
                                <mwc-list-item value="feet" ?selected=${this.preciseUnit === 'feet'}>Feet</mwc-list-item>
                                <mwc-list-item value="yards" ?selected=${this.preciseUnit === 'yards'}>Yards</mwc-list-item>
                                <mwc-list-item value="meters" ?selected=${this.preciseUnit === 'meters'}>Meters</mwc-list-item>
                                <mwc-list-item value="miles" ?selected=${this.preciseUnit === 'miles'}>Miles</mwc-list-item>
                            </proxi-selectfield-inline>
                        </div>
                        ${isRectangle ? html`
                            <div class="precise-form__row">
                                <proxi-textfield
                                    outlined
                                    type="number"
                                    label="Width"
                                    placeholder="e.g. 2"
                                    .value=${this.preciseWidth}
                                    @input=${(e: any) => { this.preciseWidth = e.target.value; }}
                                ></proxi-textfield>
                            </div>
                            <div class="precise-form__row">
                                <proxi-textfield
                                    outlined
                                    type="number"
                                    label="Height"
                                    placeholder="e.g. 2"
                                    .value=${this.preciseHeight}
                                    @input=${(e: any) => { this.preciseHeight = e.target.value; }}
                                ></proxi-textfield>
                            </div>
                        ` : html`
                            <div class="precise-form__row">
                                <proxi-textfield
                                    outlined
                                    type="number"
                                    label="Length"
                                    placeholder="e.g. 100"
                                    .value=${this.preciseLength}
                                    @input=${(e: any) => { this.preciseLength = e.target.value; }}
                                ></proxi-textfield>
                            </div>
                            <div class="precise-form__row">
                                <proxi-textfield
                                    outlined
                                    type="number"
                                    label="Direction (degrees from north)"
                                    placeholder="0 = north, 90 = east"
                                    .value=${this.preciseBearing}
                                    @input=${(e: any) => { this.preciseBearing = e.target.value; }}
                                ></proxi-textfield>
                            </div>
                        `}
                        <div class="precise-form__row" style="margin-top: 0.5rem;">
                            <form-button size="md" class="button" raised @click=${this.startPlacement}>
                                Place on Map
                            </form-button>
                        </div>
                    </div>
                `}
            </div>
        `;
    }

    renderListOfShapes() {
        return html`
            <div class="modalContent">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                    <h4>
                        Add New Shape
                    </h4>
                    <span> <a @click="${() => {this.editDefaultSettingsMode = true;}}" style="cursor: pointer; text-decoration: underline;">
                        Edit Default Style</a></span>
                </div>
                <div class="map-draw-options">
                    <div
                        class="map-draw-options__option ${this.hasEntitlement ? '' : 'disabled'}"
                        @click="${this.hasEntitlement ? this.drawNewLine : this.openUpgradeDialog}"
                    >
                        <mwc-icon class="map-draw-options__icon">polyline</mwc-icon>
                        <div class="map-draw-options__title">Line</div>
                    </div>
                    <div
                        class="map-draw-options__option ${this.hasEntitlement ? '' : 'disabled'}"
                        @click="${this.hasEntitlement ? this.drawNewPolygon : this.openUpgradeDialog}"
                    >
                        <mwc-icon class="map-draw-options__icon">change_history</mwc-icon>
                        <div class="map-draw-options__title">Polygon</div>
                    </div>
                    <div
                        class="map-draw-options__option ${this.hasEntitlement ? '' : 'disabled'}"
                        @click="${this.hasEntitlement ? this.drawNewCircle : this.openUpgradeDialog}"
                    >
                        <mwc-icon class="map-draw-options__icon">trip_origin</mwc-icon>
                        <div class="map-draw-options__title">Circle</div>
                    </div>
                    <div
                        class="map-draw-options__option ${this.hasEntitlement ? '' : 'disabled'}"
                        @click="${this.hasEntitlement ? this.addShapeGeojson : this.openUpgradeDialog}"
                    >
                        <mwc-icon class="map-draw-options__icon">code</mwc-icon>
                        <div class="map-draw-options__title">GeoJSON code</div>
                    </div>
                    <div
                        class="map-draw-options__option ${this.hasEntitlement ? '' : 'disabled'}"
                        @click="${this.hasEntitlement ? this.addRoute : this.openUpgradeDialog}"
                    >
                        <mwc-icon class="map-draw-options__icon">route</mwc-icon>
                        <div class="map-draw-options__title">Custom Route</div>
                    </div>
                </div>
                <h4>Draw by Dimensions</h4>
                <div class="map-draw-options">
                    <div
                        class="map-draw-options__option ${this.hasEntitlement ? '' : 'disabled'}"
                        @click="${this.hasEntitlement ? this.openPreciseRectangle : this.openUpgradeDialog}"
                    >
                        <mwc-icon class="map-draw-options__icon">crop_square</mwc-icon>
                        <div class="map-draw-options__title">Precise Rectangle</div>
                    </div>
                    <div
                        class="map-draw-options__option ${this.hasEntitlement ? '' : 'disabled'}"
                        @click="${this.hasEntitlement ? this.openPreciseLine : this.openUpgradeDialog}"
                    >
                        <mwc-icon class="map-draw-options__icon">straighten</mwc-icon>
                        <div class="map-draw-options__title">Precise Line</div>
                    </div>
                </div>
                <h4>Background Shapes</h4>
                <div class="modalBody">

                    ${!this.drawNewShape ? html`
                        ${drawingTool.allShapes?.sort((a, b) => (a.shape.name ?? 'a').localeCompare(b.shape.name ?? 'b')).map((shape: TopLevelShapeHolder) => html`
                            <div class="edit_row" @mouseover="${() => this.onShapeHoverStart(shape)}" @mouseleave="${() => this.onShapeHoverEnd(shape)}">
                                <mwc-icon
                                    class="edit_row_icon"
                                    style="color: ${hexToRgba(shape.shape.style?.stroke_color ?? '#000000', shape.shape.style?.stroke_opacity ?? 0)};
                                    background: ${hexToRgba(shape.shape.style?.fill_color ?? '#000000', shape.shape.style?.fill_opacity ?? 0)}">
                                    ${getShapeIconName(shape.shape.circle_details ? 'Circle' : shape.shape.geojson.type)}
                                </mwc-icon>
                                <p class="edit_row_text">${shape.shape.name} (${shape.shape.circle_details ? 'Circle': shape.shape.geojson.type})</p>
                                <div class="edit_row__actions">
                                    <form-button size="xs" @click="${async () => await this.onEditShape(shape)}">Edit</form-button>
                                    <form-button variant="outline" size="xs" @click="${async () => await this.onDuplicateShape(shape)}" icon="content_copy"></form-button>
                                    <form-button variant="outline" size="xs" @click="${async () => await this.onDeleteShape(shape)}" icon="delete"></form-button>
                                </div>
                            </div>
                            `)}
                        </div>
                    ` : ''}
                    <h4>Data Shapes</h4>
                    ${pointCache.allPoints.filter(point => point.custom_shape).map((point: GetPointResponse) => html`
                        <div class="edit_row">
                            <mwc-icon
                                class="edit_row_icon"
                                style="color: ${point.custom_shape?.style?.stroke_color ?? '#000000'};
                                background: ${point.custom_shape?.style?.fill_color ?? '#000000'}">
                                ${getShapeIconName(point.custom_shape?.circle_details ? 'Circle' : point.custom_shape?.geojson?.type ?? 'LineString')}
                            </mwc-icon>
                            <p class="edit_row_text">${point.name ?? 'New Point'}</p>
                            <div class="edit_row__actions">
                                <form-button size="xs" @click="${async () => await topicData.mapComponent?.onPointEdit(point)}">
                                    Edit Point
                                </form-button>
                            </div>
                        </div>
                    `)}
                </div>
            </div>
        `;
    }

    async addShapeAsBackgroundObject() {
        this.showSelectShapeType = false;
        await topicData.mapComponent?.addStaticShape();
    }

    async addShapeAsPoint() {
        this.showSelectShapeType = false;
        await topicData.mapComponent?.createPointFromShape();
    }

    renderSelectType() {
        return html`
            <div class="modalContent">
                <h4>How should Proxi create the shape?</h4>
                <div class="modalBody">
                    <div class="point_type_option" @click="${this.addShapeAsBackgroundObject}">
                        <mwc-icon>wallpaper</mwc-icon>
                        <b>As Background Object</b>
                        Your shape will be added to the map as a background object. This means it will be visible on the map but not interactive. It will always be visible.
                    </div>
                    <div class="point_type_option" @click="${this.addShapeAsPoint}">
                        <mwc-icon>location_searching</mwc-icon>
                        <b>As Data Point</b>
                        Your shape will behave like a point. It will be interactive and can be clicked on to view more information. It can be assigned categories, tags, and more and filtered appropriately.
                    </div>
                </div>
            </div>
        `;
    }

    onShapeHoverStart(shape: TopLevelShapeHolder) {
        setShapeOptions(shape.inner, {strokeWeight: 5, strokeOpacity: 1, strokeColor: palette.accentFeatured});
    }

    onShapeHoverEnd(shape: TopLevelShapeHolder) {
        setShapeOptions(shape.inner, {strokeWeight: shape.shape.style?.stroke_weight ?? 2, strokeOpacity: shape.shape.style?.stroke_opacity ?? 0.5, strokeColor: shape.shape.style?.stroke_color ?? '#000000'});
    }

    static get styles() {
        return css`
            .modal__title {
                font-size: 1rem;
            }

            h4 {
                margin-bottom:0.5rem;
            }

            .modal__title.center {
                text-align: center;
            }

            .form-control {
                margin: 0.5rem 0;
            }

            .modalContent {
                height: 100%;
                display: flex;
                flex-direction: column;
                overflow-y: auto;
            }

            .modalContent::-webkit-scrollbar {
                width: 5px;
            }

            .modalContent::-webkit-scrollbar-track {
                background: var(--theme-background-color);
            }

            .modalContent::-webkit-scrollbar-thumb {
                background: var(--theme-border-color);
            }


            .style-group {
                flex: 1;
                overflow: hidden scroll;
                padding-right: 0.5rem;
            }

            .style-group::-webkit-scrollbar {
                width: 5px;
            }

            .style-group::-webkit-scrollbar-track {
                background: var(--theme-background-color);
            }

            .style-group::-webkit-scrollbar-thumb {
                background: var(--theme-border-color);
            }

            .edit-mode__tabs {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 1.5rem;
            }

            .edit-mode__tab {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 600;
                line-height: 150%;
                color: var(--main-primary-color);
                background: transparent;
                padding: 0.25rem 0.5rem;
                border: 1px solid var(--main-primary-color);
                cursor: pointer;
                white-space: nowrap;
                width: 190px;
            }

            .changeToData {
                text-decoration: underline;
                cursor: pointer;
                margin-top: 1rem;
            }

            .edit-mode__tab.right {
                border-radius: 0px 20px 20px 0px;
            }

            .edit-mode__tab.left {
                border-radius: 20px 0px 0px 20px;
            }

            .edit-polygon-modal__wrapper {
                width: 100%;
                max-width: 500px;
                display: flex;
                max-height: 70vh;
                min-height: 30vh;
                flex-direction: column;
            }

            .edit-mode__tab.active {
                background: var(--main-primary-color);
                color: var(--theme-white-color);
            }

            .divider {
                height: 1px;
                width: 100%;
                margin: 1rem 0;
                background: var(--theme-border-color);
            }

            .edit_row__actions {
                display: flex;
                gap: 0.5rem;
                flex-direction: row;
            }


            .modal__title center {
                margin-top: 0.5rem;
            }

            .codeWrapper {
                margin-bottom: 1.5rem;
            }

            .buttonGroup {
                margin: 0.5rem 0;
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }

            .buttonArea {
                display: flex;
                gap: 0.5rem;
                justify-content: space-between
            }

            .modal__title center {
                margin-top: 0.5rem;
            }

            .button.red {
                --mdc-theme-primary: var(--main-invalid-color);
            }

            .button.green {
                --mdc-theme-primary: var(--main-success-color);
            }

            .error-message {
                margin: 1rem 0;
                color: var(--main-invalid-color);
            }

            .edit_row_icon {
                border-radius: 4px;
            }

            .edit_row {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 10px;
                margin: 5px 0;
                cursor: default;
            }

            .edit_row_text {
                flex: 1;
            }

            .edit_row__icon {
                border-radius: 4px;
            }

            .edit_row:hover {
                font-weight: bold;
            }

            .edit_row__image {
                width: 40px;
                height: 40;
            }

            .modalTitle {
                text-align: left;
                margin-bottom: 1rem;
                margin-top: 1rem;
            }

            .modalBody {
                flex: 1;
                overflow-y: auto;
                overflow-x: hidden;
                padding-right: 0.5rem;
            }

            .modalBody::-webkit-scrollbar {
                width: 5px;
            }

            .modalBody::-webkit-scrollbar-track {
                background: var(--theme-background-color);
            }

            .modalBody::-webkit-scrollbar-thumb {
                background: var(--theme-border-color);
            }

            .modalFooter {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                color: white;
                padding: 5px;
            }

            .point_type_option {
                border: 2px solid var(--theme-border-color);
                background-color: var(--main-featured-color);
                padding: 1rem;
                margin-bottom: 1rem;
                cursor: pointer;
                border-radius: 4px;
                display: flex;
                flex-direction: column;
            }

            .point_type_option:hover {
                border: 4px solid var(--main-primary-color) inset;
                box-shadow: 0 0 10px var(--main-primary-color);
            }

            .map-draw-options__option {
                width: 100%;
                padding: 0.25rem;
                box-sizing: border-box;
                gap: 2px;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                border-radius: 6px;
                font-size: 12px;
                text-align: center;
                cursor: pointer;
                background-color: var(--theme-border-color);
            }

            .map-draw-options__option:hover {
                background: var(--main-featured-color);
                font-weight: bold;
            }

            .map-draw-options {
                display: flex;
                flex-direction: row;
                color: var(--theme-input-color);
                border: 0;
                gap: 0.5rem;
                box-sizing: border-box;
                overflow: auto;
            }

            .disabled {
                opacity: 0.5;
            }

            .precise-form {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                padding: 0.5rem 0;
            }

            .precise-form__row {
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
            }

            .precise-form__row label {
                font-size: 12px;
                font-weight: 600;
                color: var(--theme-input-color);
            }

            .precise-placement-message {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 0.75rem;
                padding: 2rem 1rem;
                text-align: center;
                color: var(--theme-input-color);
            }

            .precise-placement-message mwc-icon {
                font-size: 48px;
                color: var(--main-primary-color);
                animation: pulse 1.5s infinite;
            }

            @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.4; }
            }

        `;
    }
}
